// Analytics loader — gated by cookie consent.
// Loads GA4, Google Tag Manager, Google Ads and Meta Pixel only after the user
// accepts analytics cookies (or has previously granted). Listens for `hc:consent`.

import { useEffect } from "react";

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const GTM_ID = process.env.REACT_APP_GTM_ID || "";
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";
const GOOGLE_ADS_BOOKING_LABEL =
  process.env.REACT_APP_GOOGLE_ADS_BOOKING_LABEL || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const CONSENT_KEY = "hc_consent";

let loaded = false;

function injectScript(src, async = true) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.src = src;
  s.async = async;
  document.head.appendChild(s);
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

function loadGoogleTags() {
  if (!GA4_ID && !GOOGLE_ADS_ID) return;
  const primaryId = GA4_ID || GOOGLE_ADS_ID;
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${primaryId}`);
  const gtag = ensureGtag();
  gtag("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
  gtag("js", new Date());
  if (GA4_ID) gtag("config", GA4_ID, { anonymize_ip: true });
  if (GOOGLE_ADS_ID) gtag("config", GOOGLE_ADS_ID);
}

function loadGTM() {
  if (!GTM_ID) return;
  // Already loaded from index.html <head> — don't double-fire the container.
  if (window.google_tag_manager && window.google_tag_manager[GTM_ID]) return;
  if (document.querySelector(`script[src*="gtm.js?id=${GTM_ID}"]`)) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });
  injectScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
}

function loadMetaPixel() {
  if (!META_PIXEL_ID || window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
}

function loadAll() {
  if (loaded) return;
  loaded = true;
  try {
    loadGoogleTags();
    loadGTM();
    loadMetaPixel();
  } catch (e) {
    // ignore — analytics should never break the page
  }
}

// Global click delegation: turns existing CTAs into conversion events without
// touching every component. Runs regardless of consent; trackEvent is a no-op
// until the tags are loaded.
function onDocumentClick(e) {
  const a = e.target?.closest?.("a[href]");
  if (!a) return;
  const href = a.getAttribute("href") || "";
  const testid = a.getAttribute("data-testid") || "";

  if (href.includes("fareharbor.com")) {
    let activity = "general";
    try {
      activity = new URL(href).searchParams.get("utm_campaign") || "general";
    } catch (_e) {
      // ignore
    }
    trackEvent("begin_checkout", {
      activity_id: activity,
      cta: testid || "book-link",
      page_path: window.location.pathname,
    });
    trackAdsConversion(GOOGLE_ADS_BOOKING_LABEL, {
      activity_id: activity,
      value: 1.0,
      currency: "USD",
    });
    if (window.fbq) window.fbq("track", "InitiateCheckout", { content_name: activity });
    return;
  }
  if (href.startsWith("https://wa.me") || href.includes("api.whatsapp.com")) {
    trackEvent("contact_whatsapp", { cta: testid, page_path: window.location.pathname });
    if (window.fbq) window.fbq("track", "Contact", { method: "whatsapp" });
    return;
  }
  if (href.startsWith("tel:")) {
    trackEvent("contact_phone", { cta: testid, page_path: window.location.pathname });
    if (window.fbq) window.fbq("track", "Contact", { method: "phone" });
    return;
  }
  if (href.startsWith("mailto:")) {
    trackEvent("contact_email", { cta: testid, page_path: window.location.pathname });
    return;
  }
  if (testid.startsWith("blogpost-cta")) {
    trackEvent("blog_cta_click", {
      destination: href,
      page_path: window.location.pathname,
    });
  }
}

export default function AnalyticsLoader() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(CONSENT_KEY) === "granted") {
        loadAll();
      }
    } catch (_e) {
      // ignore
    }
    const onConsent = (e) => {
      if (e?.detail === "granted") loadAll();
    };
    window.addEventListener("hc:consent", onConsent);
    document.addEventListener("click", onDocumentClick, true);
    return () => {
      window.removeEventListener("hc:consent", onConsent);
      document.removeEventListener("click", onDocumentClick, true);
    };
  }, []);
  return null;
}

// Helper for tracking conversion events (Book buttons, language choice, etc.)
export function trackEvent(name, params = {}) {
  try {
    if (window.gtag) window.gtag("event", name, params);
    if (window.dataLayer) window.dataLayer.push({ event: name, ...params });
    if (window.fbq) window.fbq("trackCustom", name, params);
  } catch (_e) {
    // ignore
  }
}

// Google Ads conversion. Label comes from the Ads conversion action snippet.
export function trackAdsConversion(label, params = {}) {
  if (!GOOGLE_ADS_ID || !label) return;
  try {
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: `${GOOGLE_ADS_ID}/${label}`,
        ...params,
      });
    }
  } catch (_e) {
    // ignore
  }
}
